import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { UserRole } from '../../shared/models/user-role.enum';
import { ApiError, HttpService } from '../../shared/services/http.service';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-verification',
  templateUrl: './verification.component.html',
  styleUrls: ['./verification.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
})
export class VerificationComponent implements OnInit, OnDestroy {
  verificationForm!: FormGroup;
  passwordForm!: FormGroup;
  isVerified: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  isInvalidOtp: boolean = false;
  errorMessage: string = '';
  submitted: boolean = false;
  email = '';
  resendTimer: number = 0;
  isResendDisabled: boolean = false;
  private resendInterval: any;
  isLoading: boolean = false;
  redirectEventView: boolean = false;
  redirectEventViewData: any = [];
  digits = ['digit1', 'digit2', 'digit3', 'digit4', 'digit5', 'digit6'];
  @ViewChildren('inputRefs') codeInputs!: QueryList<
    ElementRef<HTMLInputElement>
  >;
  @ViewChild('backgroundVideo', { static: false })
  backgroundVideo!: ElementRef<HTMLVideoElement>;

  private readonly RESEND_TIMER_KEY = 'verification_resend_timer';
  private readonly RESEND_START_KEY = 'verification_resend_start';
  private readonly RESEND_DURATION_KEY = 'verification_resend_duration';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.email = params['email'] || '';
    });
    const redirectEventViewDetails = localStorage.getItem('studentEvent');
    if (redirectEventViewDetails) {
      this.redirectEventView = true;
      this.redirectEventViewData = redirectEventViewDetails;
    }

    const storedVerification = localStorage.getItem('isVerified');
    this.isVerified = storedVerification === 'true';

    this.initVerificationForm();
    this.initPasswordForm();
    this.initializeResendTimer();
  }

  ngAfterViewInit(): void {
    this.initializeVideo();
    setTimeout(() => {
      if (this.codeInputs && this.codeInputs.first) {
        this.codeInputs.first.nativeElement.focus();
      }
    });
  }

  ngOnDestroy(): void {
    const video = this.backgroundVideo?.nativeElement;
    if (video) {
      video.pause();
      video.src = '';
      video.load();
    }
    if (this.resendInterval) clearInterval(this.resendInterval);
  }

  /**
   * Initialize resend timer from localStorage
   */
  private initializeResendTimer(): void {
    const savedStartTime = localStorage.getItem(this.RESEND_START_KEY);
    const savedDuration = localStorage.getItem(this.RESEND_DURATION_KEY);

    if (savedStartTime && savedDuration) {
      const startTime = parseInt(savedStartTime, 10);
      const duration = parseInt(savedDuration, 10);
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - startTime) / 1000);

      if (elapsedSeconds < duration) {
        const remaining = duration - elapsedSeconds;
        this.startResendTimer(remaining);
      } else {
        this.clearTimerData();
      }
    }
  }

  /**
   * Clear timer data from localStorage
   */
  private clearTimerData(): void {
    localStorage.removeItem(this.RESEND_TIMER_KEY);
    localStorage.removeItem(this.RESEND_START_KEY);
    localStorage.removeItem(this.RESEND_DURATION_KEY);
  }

  /**
   * Save timer state to localStorage
   */
  private saveTimerState(
    remainingSeconds: number,
    totalDuration: number,
  ): void {
    const now = Date.now();
    localStorage.setItem(this.RESEND_START_KEY, now.toString());
    localStorage.setItem(this.RESEND_DURATION_KEY, totalDuration.toString());
    localStorage.setItem(this.RESEND_TIMER_KEY, remainingSeconds.toString());
  }

  private initializeVideo(): void {
    const video = this.backgroundVideo?.nativeElement;
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    video.loop = true;
    const playVideo = () => {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          // console.log('Video playing successfully');
        });
      }
    };
    if (video.readyState >= 3) {
      playVideo();
    } else {
      video.addEventListener('loadeddata', playVideo, { once: true });
    }
    video.addEventListener('pause', () => {
      if (!video.ended) {
        video.play().catch((e) => console.warn('Resume failed:', e));
      }
    });
  }

  initVerificationForm(): void {
    this.verificationForm = this.fb.group(
      Object.fromEntries(
        this.digits.map((d) => [
          d,
          ['', [Validators.required, Validators.pattern('[0-9]')]],
        ]),
      ),
    );
  }

  initPasswordForm(): void {
    this.passwordForm = this.fb.group(
      {
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/,
            ),
          ],
        ],
        confirmPassword: ['', Validators.required],
      },
      { validators: this.passwordMatchValidator },
    );
  }

  passwordMatchValidator(form: FormGroup) {
    return form.get('password')?.value === form.get('confirmPassword')?.value
      ? null
      : { mismatch: true };
  }

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;

    let value = input.value.replace(/\D/g, '');
    value = value.replace(/\s/g, '');

    if (value.length > 1) {
      input.value = value[0];
      this.verificationForm.get(this.digits[index])?.setValue(value[0]);
    } else {
      input.value = value;
      this.verificationForm.get(this.digits[index])?.setValue(value);
    }

    if (value && index < this.digits.length - 1) {
      this.codeInputs.toArray()[index + 1].nativeElement.focus();
    }

    this.isInvalidOtp = false;
    this.errorMessage = '';
  }

  onOtpKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === ' ') {
      event.preventDefault();
      return;
    }

    if (event.key === 'Backspace' && !input.value && index > 0) {
      const prev = this.codeInputs.toArray()[index - 1].nativeElement;
      prev.focus();
      prev.value = '';
      this.verificationForm.get(this.digits[index - 1])?.setValue('');
    }
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '')
      .replace(/\D/g, '')
      .replace(/\s/g, '');

    if (pasted.length === this.digits.length) {
      const inputs = this.codeInputs.toArray();
      this.digits.forEach((d, i) => {
        this.verificationForm.get(d)?.setValue(pasted[i]);
        inputs[i].nativeElement.value = pasted[i];
      });
    }
  }

  getVerificationCode(): string {
    return Object.values(this.verificationForm.value).join('');
  }

  onVerify(): void {
    this.submitted = true;
    const deviceId = localStorage.getItem('deviceId');
    const credentials = localStorage.getItem('rememberedCredentials');
    let storedPass;
    if (credentials) {
      try {
        const parsedCredentials = JSON.parse(credentials);
        storedPass = parsedCredentials.password;
      } catch (error) {
        storedPass = undefined;
      }
    }
    if (this.verificationForm.valid && !this.isLoading) {
      this.isLoading = true;
      const payload = {
        email: this.email,
        code: this.getVerificationCode(),
        deviceId: deviceId,
        p_key: storedPass,
      };
      this.httpService.post<any>(`auth/login/2fa`, payload).subscribe({
        next: (response: any) => {
          if (response?.status) {
            const data = response.data;
            console.log(response);

            const normalizedRole = data.role
              .trim()
              .toLowerCase()
              .replace(/\s/g, '');

            let userRole: UserRole;
            switch (normalizedRole) {
              case 'superadmin':
                userRole = UserRole.SuperAdmin;
                break;
              case 'csqadmin':
              case 'CSQ Admin':
                userRole = UserRole.CsqAdmin;
                break;
              case 'csquser':
                userRole = UserRole.CsqUser;
                break;
              case 'teacher':
                userRole = UserRole.Teacher;
                break;
              case 'student':
                userRole = UserRole.Student;
                break;
              default:
                userRole = UserRole.Student;
                break;
            }
            this.authService.setSession(
              data.accessToken,
              data.refreshToken,
              data.expiresAt,
            );

            if (response.data?.role === 'Teacher') {
              localStorage.setItem(
                'teacherIsFirstLogin',
                response.data?.isFirstLogin === true ? 'true' : 'false',
              );
            } else if (response.data?.role === 'Student') {
              localStorage.setItem(
                'studentIsFirstLogin',
                response.data?.isFirstLogin === true ? 'true' : 'false',
              );
            }

            this.authService.setUserRole(userRole);
            localStorage.setItem(
              'userDetails',
              JSON.stringify({
                userId: data.userId,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                profileImageUrl: data.profileImageUrl,
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiresAt: data.expiresAt,
                role: userRole,
                requiresTwoFactor: data.requiresTwoFactor,
                schoolId: data.schoolId,
              }),
            );

            this.clearTimerData();

            const parsed =
              typeof this.redirectEventViewData === 'string'
                ? JSON.parse(this.redirectEventViewData)
                : this.redirectEventViewData;

            const eventId = parsed?.eventId;
            if (this.redirectEventView && userRole === UserRole.Student) {
              const segment =
                localStorage.getItem('studentEventSegment') || 'admin';
              localStorage.removeItem('studentEventSegment');
              this.router.navigate(['/events/event-list/view', eventId], {
                queryParams: { segment },
              });
            } else {
              this.authService.redirectByRole(userRole).then(() => {
                this.isLoading = false;
                this.showSuccessToast(response.message);
                const redirectEventId = this.authService.getRedirectEventId();
                const redirectEventSegment =
                  this.authService.getRedirectSegment();
                if (redirectEventId) {
                  this.authService.clearRedirectData();
                  this.router.navigate(
                    ['/events/event-list/view', redirectEventId],
                    { queryParams: { segment: redirectEventSegment } },
                  );
                }
              });
            }
          }
        },
        error: (error: ApiError) => {
          this.isLoading = false;
          this.isInvalidOtp = true;
          this.errorMessage = error.message || 'Invalid OTP. Please try again.';
          this.showErrorToast(this.errorMessage);
        },
      });
    } else {
      this.isInvalidOtp = true;
      this.errorMessage = 'Please enter a valid input';
      this.verificationForm.markAllAsTouched();
    }
  }

  resendCode(): void {
    if (this.isResendDisabled) return;
    const deviceId = localStorage.getItem('deviceId');
    const credentials = localStorage.getItem('rememberedCredentials');
    let storedPass;
    if (credentials) {
      try {
        const parsedCredentials = JSON.parse(credentials);
        storedPass = parsedCredentials.password;
      } catch (error) {
        storedPass = undefined;
      }
    }
    this.isInvalidOtp = false;
    this.errorMessage = '';
    // this.isLoading = true;

    const payload = {
      email: this.email,
      deviceId: deviceId,
      p_key: storedPass,
    };
    console.log(payload, 'payload');

    this.httpService.post<any>('auth/login/2fa/resend', payload).subscribe({
      next: (response: any) => {
        // this.isLoading = false;
        if (response?.status) {
          this.showSuccessToast(response.message);
          this.startResendTimer(60);
        }
      },
      error: (error: ApiError) => {
        // this.isLoading = false;
        this.showErrorToast(error.message);
      },
    });
  }

  private startResendTimer(seconds: number): void {
    this.isResendDisabled = true;
    this.resendTimer = seconds;
    this.saveTimerState(seconds, seconds);

    if (this.resendInterval) clearInterval(this.resendInterval);

    this.resendInterval = setInterval(() => {
      if (this.resendTimer > 0) {
        this.resendTimer--;
        localStorage.setItem(
          this.RESEND_TIMER_KEY,
          this.resendTimer.toString(),
        );
      } else {
        this.isResendDisabled = false;
        clearInterval(this.resendInterval);
        this.clearTimerData();
      }
    }, 1000);
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
